/**
 * The merge fields a design email may use, per kind — the builder's insert
 * palette, derived from the SHARED kind registry (the same list the backend
 * validates at publish and the send populates), grouped by namespace with
 * friendly headings.
 */
import {
  BUSINESS_FIELDS,
  EMAIL_KINDS,
  EMAIL_TEMPLATE_KINDS,
  MERGE_FIELD_LABELS,
  type EmailTemplateKind,
} from '@notifi/shared-types/email-kinds';

export interface MergeField {
  /** The token inserted, without braces — e.g. `invoice.number`. */
  token: string;
  label: string;
}

export interface MergeFieldGroup {
  heading: string;
  fields: MergeField[];
}

const NAMESPACE_HEADINGS: Record<string, string> = {
  business: 'Business',
  customer: 'Customer',
  estimate: 'Estimate',
  invoice: 'Invoice',
  appointment: 'Appointment',
  receipt: 'Receipt',
  proposal: 'Proposal',
  changeOrder: 'Change order',
  booking: 'Booking',
  review: 'Review',
  portal: 'Portal',
  membership: 'Membership',
  renewal: 'Renewal',
  payment: 'Payment',
  paymentMethod: 'Payment method',
  verify: 'Verification',
  report: 'Report',
  legal: 'Legal (supplied by the send)',
};

function groupsFor(kind: EmailTemplateKind): MergeFieldGroup[] {
  const tokens = [...BUSINESS_FIELDS, ...EMAIL_KINDS[kind].fields];
  const byNs = new Map<string, MergeField[]>();
  for (const token of tokens) {
    const ns = token.split('.')[0] ?? token;
    const list = byNs.get(ns) ?? [];
    list.push({ token, label: MERGE_FIELD_LABELS[token] ?? token });
    byNs.set(ns, list);
  }
  return [...byNs.entries()].map(([ns, fields]) => ({
    heading: NAMESPACE_HEADINGS[ns] ?? ns,
    fields,
  }));
}

export const MERGE_FIELD_GROUPS: Record<EmailTemplateKind, MergeFieldGroup[]> = Object.fromEntries(
  EMAIL_TEMPLATE_KINDS.map((k) => [k, groupsFor(k)]),
) as Record<EmailTemplateKind, MergeFieldGroup[]>;

/** Wrap a bare token in the `{{ }}` the resolver expects. */
export const mergeToken = (token: string): string => `{{${token}}}`;

/**
 * The same fields shaped for the "Insert variable" menu (VariablePicker). The
 * legal texts are left out: the send supplies them through the locked Legal
 * block, so they are not something to type into a paragraph.
 */
export function mergeFieldPickerGroups(
  kind: EmailTemplateKind,
): { label: string; vars: { label: string; value: string }[] }[] {
  return MERGE_FIELD_GROUPS[kind]
    .filter((group) => group.fields.some((f) => !f.token.startsWith('legal.')))
    .map((group) => ({
      label: group.heading,
      vars: group.fields
        .filter((f) => !f.token.startsWith('legal.'))
        .map((f) => ({ label: f.label, value: mergeToken(f.token) })),
    }));
}
